import { Box, keyframes } from '@mui/material'
import React from 'react'
import businessAddress from '../images/tour/scenes/business-address.webp'
import businessAvailability from '../images/tour/scenes/business-availability.webp'
import businessBack from '../images/tour/scenes/business-back.webp'
import businessDetails from '../images/tour/scenes/business-details.webp'
import businessMobile from '../images/tour/scenes/business-mobile.webp'
import businessName from '../images/tour/scenes/business-name.webp'
import businessPosters from '../images/tour/scenes/business-posters.webp'
import businessProfile from '../images/tour/scenes/business-profile.webp'
import businessReservations from '../images/tour/scenes/business-reservations.webp'
import businessSave from '../images/tour/scenes/business-save.webp'
import businessShare from '../images/tour/scenes/business-share.webp'
import publicBookings from '../images/tour/scenes/public-bookings.webp'
import publicFavorites from '../images/tour/scenes/public-favorites.webp'
import publicProfessional from '../images/tour/scenes/public-professional.webp'
import publicReady from '../images/tour/scenes/public-ready.webp'
import publicSearch from '../images/tour/scenes/public-search.webp'
import publicSupport from '../images/tour/scenes/public-support.webp'
import publicWelcome from '../images/tour/scenes/public-welcome.webp'

export type AnnaTourScene =
    | 'public-welcome'
    | 'public-search'
    | 'public-favorites'
    | 'public-bookings'
    | 'public-support'
    | 'public-professional'
    | 'public-ready'
    | 'business-profile'
    | 'business-details'
    | 'business-availability'
    | 'business-posters'
    | 'business-share'
    | 'business-reservations'
    | 'business-name'
    | 'business-address'
    | 'business-mobile'
    | 'business-save'
    | 'business-back'

type AnnaTourMessageProps = {
    children: React.ReactNode
    scene: AnnaTourScene
}

export const AnnaTourMessage: React.FC<AnnaTourMessageProps> = ({
    children,
}) => <>{children}</>

type AnnaTourContentProps = {
    content:
        | React.ReactNode
        | ((props: Omit<AnnaTourContentProps, 'content'>) => React.ReactNode)
    setCurrentStep: React.Dispatch<React.SetStateAction<number>>
    setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>
    currentStep: number
    transition?: boolean
    isHighlightingObserved?: boolean
}

const drift = keyframes`
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50% { transform: translateY(-4px) rotate(0.7deg); }
`

const gesture = keyframes`
    0%, 100% { transform: translateX(0) rotate(0deg); }
    45% { transform: translateX(-3px) rotate(-0.8deg); }
    70% { transform: translateX(-1px) rotate(0.4deg); }
`

const celebrate = keyframes`
    0%, 100% { transform: translateY(0) scale(1); }
    38% { transform: translateY(-6px) scale(1.02); }
    62% { transform: translateY(0) scale(0.99); }
`

const annaScenes: Record<
    AnnaTourScene,
    { src: string; animation: ReturnType<typeof keyframes> }
> = {
    'public-welcome': { src: publicWelcome, animation: gesture },
    'public-search': { src: publicSearch, animation: drift },
    'public-favorites': { src: publicFavorites, animation: gesture },
    'public-bookings': { src: publicBookings, animation: drift },
    'public-support': { src: publicSupport, animation: gesture },
    'public-professional': { src: publicProfessional, animation: gesture },
    'public-ready': { src: publicReady, animation: celebrate },
    'business-profile': { src: businessProfile, animation: gesture },
    'business-details': { src: businessDetails, animation: drift },
    'business-availability': { src: businessAvailability, animation: drift },
    'business-posters': { src: businessPosters, animation: gesture },
    'business-share': { src: businessShare, animation: gesture },
    'business-reservations': {
        src: businessReservations,
        animation: drift,
    },
    'business-name': { src: businessName, animation: gesture },
    'business-address': { src: businessAddress, animation: drift },
    'business-mobile': { src: businessMobile, animation: gesture },
    'business-save': { src: businessSave, animation: celebrate },
    'business-back': { src: businessBack, animation: gesture },
}

const isAnnaTourMessage = (
    content: React.ReactNode
): content is React.ReactElement<AnnaTourMessageProps> =>
    React.isValidElement<AnnaTourMessageProps>(content) &&
    content.type === AnnaTourMessage

const AnnaTourContent: React.FC<AnnaTourContentProps> = (props) => {
    const {
        content,
        currentStep,
        setCurrentStep,
        setIsOpen,
        transition,
        isHighlightingObserved,
    } = props
    const renderedContent =
        typeof content === 'function'
            ? content({
                  currentStep,
                  setCurrentStep,
                  setIsOpen,
                  transition,
                  isHighlightingObserved,
              })
            : content
    const sceneName = isAnnaTourMessage(renderedContent)
        ? renderedContent.props.scene
        : 'public-welcome'
    const scene = annaScenes[sceneName]

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 1.25, sm: 2 },
                minHeight: { xs: 128, sm: 152 },
                pr: 0.5,
            }}
        >
            <Box
                aria-hidden="true"
                sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    flex: '0 0 auto',
                    width: { xs: 112, sm: 140 },
                    height: { xs: 112, sm: 140 },
                    overflow: 'hidden',
                    borderRadius: 2,
                    background:
                        'linear-gradient(145deg, rgba(111, 188, 158, 0.2), rgba(111, 188, 158, 0.05))',
                }}
            >
                <Box
                    key={`${sceneName}-${currentStep}`}
                    component="img"
                    src={scene.src}
                    alt=""
                    sx={{
                        display: 'block',
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        objectPosition: 'center bottom',
                        transformOrigin: '50% 100%',
                        animation: `${scene.animation} 2.7s ease-in-out infinite`,
                        '@media (prefers-reduced-motion: reduce)': {
                            animation: 'none',
                        },
                    }}
                />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>{renderedContent}</Box>
        </Box>
    )
}

export default AnnaTourContent
